import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import Markdown from "markdown-to-jsx";
import { color } from "src/styles/variables";
import Container from "src/react/layout/container";
import { Section } from "src/react/layout/section";
import Stars from "src/react/components/stars";
import ProductStars from "./product-stars";

const MINIMUM_RATING = 4;

function topReviewsByRating(reviews = [], amount) {
  return reviews
    .filter((review) => parseFloat(review.rating) >= MINIMUM_RATING)
    .sort((a, b) => {
      const ratingA = parseFloat(a.rating);
      const ratingB = parseFloat(b.rating);
      if (ratingA === ratingB) {
        return a.product_review_id > b.product_review_id ? -1 : 1;
      }
      return ratingA > ratingB ? -1 : 1;
    })
    .slice(0, amount);
}

function Reviews({ reviews = {}, stats = {} }) {
  const topTenReviews = topReviewsByRating(reviews.data, 10);
  return (
    !!topTenReviews.length && (
      <Section
        id="reviews"
        backgroundColor={color.lightGrey}
        margin="0 0 40px 0"
      >
        <ReviewsContainer>
          <ReviewsContainerHeader>
            <h2>Top reviews</h2>
            <ProductStars
              rating={parseFloat(stats.average || 0)}
              reviewCount={stats.count}
            />
          </ReviewsContainerHeader>
          {topTenReviews.map((review) => (
            <ProductReview key={review.product_review_id} review={review} />
          ))}
        </ReviewsContainer>
      </Section>
    )
  );
}
Reviews.propTypes = {
  reviews: PropTypes.object,
  stats: PropTypes.object,
};

export default Reviews;

export function ProductReview({ review }) {
  return (
    <FlexWrapper>
      <AuthorWrapper>
        <Author>{review.reviewer.first_name}</Author>
        <ViaLink
          href={`https://www.reviews.co.uk/product-reviews/store/cornerstone/${review.product.sku}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          reviews.co.uk
        </ViaLink>
      </AuthorWrapper>
      <ReviewWrapper>
        <Stars value={parseFloat(review.rating)} starSize={20} />
        <ReviewContent>{review.review}</ReviewContent>
      </ReviewWrapper>
    </FlexWrapper>
  );
}
ProductReview.propTypes = {
  review: PropTypes.object,
};

const breakpoint = "@media (min-width: 650px)";

const FlexWrapper = styled.article`
  margin-bottom: 15px;
  background-color: ${color.white};
  padding: 15px;
  border-radius: 5px;
  ${breakpoint} {
    display: flex;
  }
  :first-child {
    margin-top: 15px;
  }
`;

const ReviewsContainer = styled(Container)`
  h2 {
    font-weight: 600;
    font-size: 22px;
    @media (min-width: 768px) {
      font-size: 24px;
    }
  }
`;

const ReviewsContainerHeader = styled.header`
  font-weight: 600;
  margin-bottom: 15px;
`;

const AuthorWrapper = styled.div`
  margin-bottom: 10px;
  ${breakpoint} {
    flex: 1;
  }
`;

const ReviewWrapper = styled.section`
  ${breakpoint} {
    flex: 3;
  }
`;

const ReviewContent = styled(Markdown)`
  display: block;
  margin-top: 5px;
`;

const Author = styled.div`
  font-weight: 600;
  font-size: 15px;
`;

const ViaLink = styled.a`
  display: block;
  text-decoration: underline;
`;
